/**
 * useServerPaging — page / page-size state for a server-paged list (#1000).
 *
 * The Contacts and Companies lists ask the API for one page at a time
 * (`page`, `page_size`). The page size is remembered per list in localStorage
 * (try/catch: a private window or blocked storage just falls back to the
 * default); the page is not — every visit starts on page 1.
 */
import { useCallback, useState } from 'react';

export const PAGE_SIZE_OPTIONS = [25, 50, 100];
export const DEFAULT_PAGE_SIZE = 50;

function readPageSize(storageKey) {
    try {
        const stored = Number(window.localStorage.getItem(storageKey));
        return PAGE_SIZE_OPTIONS.includes(stored) ? stored : DEFAULT_PAGE_SIZE;
    } catch {
        return DEFAULT_PAGE_SIZE;
    }
}

export default function useServerPaging(storageKey) {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSizeState] = useState(() => readPageSize(storageKey));

    // A new size starts again at page 1, so the first row on screen stays the first row.
    const setPageSize = useCallback((size) => {
        setPageSizeState(size);
        setPage(1);
        try { window.localStorage.setItem(storageKey, String(size)); } catch { /* not remembered */ }
    }, [storageKey]);

    const resetPage = useCallback(() => setPage(1), []);

    // After a load: a page past the end (its rows were deleted or filtered away
    // since) moves to the last page that still has rows, never an empty table;
    // an empty list goes back to page 1, so the pager never reads "page 2 of 1".
    const clampAfterLoad = useCallback((rowCount, total) => {
        if (total <= 0) { setPage(1); return; }
        if (rowCount > 0) return;
        const last = Math.max(1, Math.ceil(total / pageSize));
        setPage(p => (p > last ? last : p));
    }, [pageSize]);

    const totalPages = useCallback((total) => Math.max(1, Math.ceil(total / pageSize)), [pageSize]);

    return { page, pageSize, setPage, setPageSize, resetPage, clampAfterLoad, totalPages };
}
